import * as _ from 'underscore';

import { DropdownType, DropdownWidget, InlineBlock, MenuItemInfo } from '@tableau/widgets';
import * as React from 'react';

import { CreatorTier, ExplorerTier, LicensingTier, LicensingTierLocPrefix, SiteRoleOption, UnlicensedTier, ViewerTier } from '../../../ts/resources/SiteRoleOptions';
import { SiteRolesResource } from '../../../ts/resources/SiteRoles';
import { CommonStyles } from '../CommonStyles';
import { IReactTranslateService } from '../IReactTranslateService';

const DefaultDropdownMenuWidth = 250;
const TierSections = [CreatorTier, ExplorerTier, ViewerTier, UnlicensedTier];
const SectionDivider = {isDivider: true};
const TwoSpaces = '\u00A0\u00A0';
const SiteRoleIndentation = TwoSpaces;
const HeaderTestIdSuffix = 'Header';

export interface SiteRolePickerMenuState {
  currentSiteRoleSelection: string;
}

/**
 * A drop down menu that groups site roles based on subscription licensing tiers
 * TODO: Replace with new dropdown shared widgets when completed (TFSID 756240)
 */
export class SiteRolePickerMenu extends React.Component<SiteRolePickerMenu.Props, SiteRolePickerMenuState> {

  public boundSelectSiteRole: (value: string) => void = value => this.handleSelection(value);

  public constructor(props: SiteRolePickerMenu.Props) {
    super(props);
    this.state = {currentSiteRoleSelection: props.selectedRole.role};
  }

  public componentWillReceiveProps(nextProps: SiteRolePickerMenu.Props) {
    this.setState({currentSiteRoleSelection: nextProps.selectedRole.role});
  }

  protected handleSelection(newOption: string): void {
    this.setState({currentSiteRoleSelection: newOption});
    const selectedRoleOption: SiteRoleOption = _.find(this.props.siteRoleOptions, (option: SiteRoleOption) => {
        return newOption === option.role;
    });
    this.props.onOptionSelected(selectedRoleOption);
  }

  public render(): JSX.Element {
    return (
        React.createElement(InlineBlock, {},
            React.createElement(DropdownWidget, {
              containerStyle: {
                width: DefaultDropdownMenuWidth - CommonStyles.Sizes.DefaultSpace - (2 * CommonStyles.Sizes.TextBoxBorderSize),
                fontFamily: CommonStyles.Fonts.HeavyFont
              },
              disabled: this.props.isDisabled,
              selectedValue: this.state.currentSiteRoleSelection,
              onSelect: this.boundSelectSiteRole,
              dropdownType: DropdownType.Outline,
              testId: 'site-role-picker',
              menuItemInfos: this.getMenuSiteRoleInfo(),
              placeholder: this.getPlaceHolder()
            })
        )
    );
  }

  protected getMenuSiteRoleInfo(): MenuItemInfo[] {
    let siteRoleOptions: MenuItemInfo[] = [];
    for (let i = 0; i < TierSections.length; i++) {
      const tier = TierSections[i];
      const siteRoleOptionsForTier = this.getSiteRoleOptionsForTier(tier);
      if (siteRoleOptionsForTier.length > 0 && tier !== UnlicensedTier){
        const tierSectionHeader = this.getHeaderForTierSection(tier);
        siteRoleOptions = siteRoleOptions.concat(tierSectionHeader);
      }
      siteRoleOptions = siteRoleOptions.concat(siteRoleOptionsForTier);
      if (i < TierSections.length-1 && siteRoleOptionsForTier.length > 0) {
        siteRoleOptions = siteRoleOptions.concat(SectionDivider);
      }
    }
    return siteRoleOptions;
  }

  private getHeaderForTierSection(tierRole: LicensingTier) {
    return {
      value: tierRole + HeaderTestIdSuffix,
      displayValue: this.props.translate(LicensingTierLocPrefix + tierRole),
      disabled: true
    };
  }

  private getSiteRoleOptionsForTier(tierRole: LicensingTier): MenuItemInfo[]{
    const siteRoleOptionsForTier = _.filter(this.props.siteRoleOptions, (siteRoleOption: SiteRoleOption) => {
      return siteRoleOption.licenseTierRole === tierRole;
    });
    return siteRoleOptionsForTier.map((siteRoleOption: SiteRoleOption) => {
        return {
          value: siteRoleOption.role,
          displayValue: this.getSiteRoleOptionDisplayValue(siteRoleOption),
          disabled: this.checkSiteRoleDisabled(siteRoleOption)
        };
    });
  }

  private getSiteRoleOptionDisplayValue(siteRoleOption: SiteRoleOption) {
    const siteRoleDisplayValue = this.props.translate(siteRoleOption.locKey);
    if (siteRoleOption.licenseTierRole === UnlicensedTier) {
      return siteRoleDisplayValue;
    }
    return SiteRoleIndentation + siteRoleDisplayValue;
  }

  private checkSiteRoleDisabled(siteRoleOption: SiteRoleOption){
      if(siteRoleOption.disabled){
        return true;
      }
      if(this.props.disabledRoleOptions){
        for (let i = 0; i < this.props.disabledRoleOptions.length; i++) {
          if (this.props.disabledRoleOptions[i].licenseTierRole === siteRoleOption.licenseTierRole &&
              this.props.disabledRoleOptions[i].locKey === siteRoleOption.locKey &&
              this.props.disabledRoleOptions[i].role === siteRoleOption.role) {
              return true;
          }
        }
      }
      return false;
  }

  private getPlaceHolder(): string{
    const siteRoleOption = this.props.getSiteRoleOptionBySiteRole(this.state.currentSiteRoleSelection);
    if(siteRoleOption != undefined && siteRoleOption.role === SiteRolesResource.Mixed){
        return this.props.translate(siteRoleOption.locKey);
    }
    return undefined;
  }
}

export namespace SiteRolePickerMenu {

  export interface Props {
    translate: IReactTranslateService;
    siteRoleOptions: SiteRoleOption[];
    getSiteRoleOptionBySiteRole: (siteRole: string) => SiteRoleOption;
    onOptionSelected: (siteRoleOption: SiteRoleOption) => void;
    selectedRole: SiteRoleOption;
    style?: React.CSSProperties;
    panelStyle?: React.CSSProperties;
    tbTestId?: string;
    disabledRoleOptions?: SiteRoleOption[];
    isDisabled?: boolean;
  }

}
